import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ChapterQuiz from '../components/ChapterQuiz';
import VoiceQuizButton from '../components/VoiceQuizButton';

const RegulationPage = () => {
  const { regulationId } = useParams();
  const [regulation, setRegulation] = useState(null);
  const [currentView, setCurrentView] = useState('quiz');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadRegulation = async () => {
      try {
        const regulationData = await import(`../regulations/${regulationId}.json`);
        setRegulation(regulationData.default);
        setLoading(false);
      } catch (err) {
        setError('Regulation not found');
        setLoading(false);
      }
    };

    loadRegulation();
  }, [regulationId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl text-gray-600">Loading regulation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/" className="text-blue-600 hover:underline">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-blue-600 hover:underline">
              ← Back to Home
            </Link>
            
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:space-x-4 sm:gap-0">
              <button
                onClick={() => setCurrentView('quiz')}
                className={`px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base min-h-[44px] ${
                  currentView === 'quiz'
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-200 text-amber-800 hover:bg-amber-300'
                }`}
              >
                Quiz
              </button>
              <button
                onClick={() => setCurrentView('voice')}
                className={`px-3 sm:px-4 py-2 rounded-md transition-colors text-sm sm:text-base min-h-[44px] ${
                  currentView === 'voice'
                    ? 'bg-amber-600 text-white'
                    : 'bg-amber-200 text-amber-800 hover:bg-amber-300'
                }`}
              >
                🎤 Voice Assessment
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto">
          <header className="mb-6 sm:mb-8">
            
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {regulation.title}
            </h1>
            {regulation.subtitle && (
              <p className="text-lg sm:text-xl text-gray-600 mb-4">
                {regulation.subtitle}
              </p>
            )}
            
            {regulation.objectives && currentView === 'content' && (
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-md">
                <h3 className="font-semibold text-amber-900 mb-2">
                  Learning Objectives
                </h3>
                <ul className="list-disc list-inside text-amber-800 text-sm space-y-1">
                  {regulation.objectives.map((objective, index) => (
                    <li key={index}>{objective}</li>
                  ))}
                </ul>
              </div>
            )}
          </header>

          {currentView === 'quiz' && (
            <ChapterQuiz quiz={regulation.quiz} />
          )}
          
          {currentView === 'voice' && (
            <VoiceQuizButton chapter={regulation} />
          )}
        </div>
      </main>
    </div>
  );
};

export default RegulationPage;